import React, { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ReferenceArea, Area, ComposedChart, Scatter } from 'recharts';
import {
  calculateAverage,
  calculateMedian,
  calculateInRangePercentage,
  calculateLinearRegression
} from '../../utils/latenessUtils';

// Helper to calculate standard deviation
const calculateStdDev = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Helper to calculate symmetric Y-axis range based on regression + std dev
const getSymmetricYDomainForRegression = (regressionData) => {
  if (!regressionData || regressionData.length === 0) return [-50, 50];
  
  // Find max absolute value considering regression ± std dev
  let maxAbs = 0;
  regressionData.forEach(point => {
    const upper = Math.abs(point.value + (point.stdDev || 0));
    const lower = Math.abs(point.value - (point.stdDev || 0));
    maxAbs = Math.max(maxAbs, upper, lower);
  });
  
  const rangeWithPadding = maxAbs * 1.2; // Add 20% padding
  
  // Round up to the nearest nice number
  let range;
  if (rangeWithPadding <= 10) {
    range = Math.ceil(rangeWithPadding);
  } else if (rangeWithPadding <= 25) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 50) {
    range = Math.ceil(rangeWithPadding / 10) * 10;
  } else if (rangeWithPadding <= 100) {
    range = Math.ceil(rangeWithPadding / 20) * 20;
  } else {
    range = Math.ceil(rangeWithPadding / 50) * 50;
  }
  
  return [-range, range];
};

// Helper to generate Y-axis ticks that are perfectly symmetrical around 0
const generateYTicks = (domain) => {
  const [min, max] = domain;
  const range = max - min;
  
  // Determine step size based on range
  let step;
  if (range <= 10) {
    step = 2;
  } else if (range <= 25) {
    step = 5;
  } else if (range <= 50) {
    step = 10;
  } else if (range <= 100) {
    step = 20;
  } else {
    step = 50;
  }
  
  // Generate ticks symmetrically from 0 outward
  const ticks = [0];
  
  // Add positive ticks
  for (let i = step; i <= max; i += step) {
    ticks.push(i);
  }
  
  // Add negative ticks
  for (let i = -step; i >= min; i -= step) {
    ticks.unshift(i);
  }
  
  return ticks;
};

const LatenessBreakout = ({ sessions, selection, onClose }) => {
  const { entity, exercise, exerciseType } = selection;

  // Filter sessions
  const filteredSessions = useMemo(() => {
    return sessions.filter(s => {
      if (exercise) {
        return s.exercise_name === exercise;
      } else if (exerciseType) {
        return s.exercise_type === exerciseType;
      }
      return true;
    });
  }, [sessions, exercise, exerciseType]);

  // Determine field name
  const field = `${entity}_lateness_avg`;
  const stdDevField = `${entity}_lateness_std_dev`;

  // Calculate statistics
  const avg = calculateAverage(filteredSessions, field);
  const median = calculateMedian(filteredSessions, field);
  const avgStdDev = calculateAverage(filteredSessions, stdDevField);
  const inRangePercent = calculateInRangePercentage(filteredSessions, field, 5); // ±5ms for lateness

  // Calculate parent averages
  const parentHandField = entity.includes('finger') 
    ? entity.split('_finger_')[0] + '_lateness_avg'
    : null;
  const parentHandAvg = parentHandField ? calculateAverage(filteredSessions, parentHandField) : null;

  // Calculate parent type average (if exercise is selected)
  const parentTypeAvg = exercise
    ? calculateAverage(
        sessions.filter(s => s.exercise_type === exerciseType),
        field
      )
    : null;

  // Prepare time series data
  const timeSeriesData = useMemo(() => {
    return filteredSessions
      .filter(s => s[field] != null)
      .map(s => ({
        timestamp: new Date(s.created_at).getTime(),
        date: new Date(s.created_at).toLocaleString(undefined, { 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric',
          minute: '2-digit',
          hour12: true 
        }),
        value: s[field]
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [filteredSessions, field]);

  // Calculate regression for time series with std dev band
  const timeRegressionData = useMemo(() => {
    if (timeSeriesData.length < 2) return [];
    
    const points = timeSeriesData.map(d => ({ x: d.timestamp, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = timeSeriesData.map(d => d.value);
    const stdDev = calculateStdDev(values);
    
    const minX = Math.min(...timeSeriesData.map(d => d.timestamp));
    const maxX = Math.max(...timeSeriesData.map(d => d.timestamp));
    
    // Create multiple points for smooth line
    const regressionPoints = [];
    const step = (maxX - minX) / 20;
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        timestamp: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [timeSeriesData]);

  // Prepare tempo scatter data (raw data points)
  const tempoScatterData = useMemo(() => {
    return filteredSessions
      .filter(s => s[field] != null && s.calculated_tempo != null)
      .map(s => ({
        tempo: s.calculated_tempo,
        value: s[field]
      }));
  }, [filteredSessions, field]);

  // Prepare tempo regression data with std dev band
  const tempoRegressionData = useMemo(() => {
    if (tempoScatterData.length < 2) return [];
    
    const points = tempoScatterData.map(d => ({ x: d.tempo, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = tempoScatterData.map(d => d.value);
    const stdDev = calculateStdDev(values);
    
    const minX = Math.min(...tempoScatterData.map(d => d.tempo));
    const maxX = Math.max(...tempoScatterData.map(d => d.tempo));
    
    // Create multiple points for smooth line
    const regressionPoints = [];
    const step = (maxX - minX) / 20;
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        tempo: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [tempoScatterData]);

  // Prepare repetition scatter data (raw data points)
  const repetitionScatterData = useMemo(() => {
    const validSessions = filteredSessions.filter(s => s[field] != null);
    return validSessions.map((s, idx) => ({
      repetition: validSessions.length - idx, // Reverse so 1 is oldest
      value: s[field]
    })).reverse(); // Reverse so repetition 1 is first
  }, [filteredSessions, field]);

  // Prepare repetition regression data with std dev band
  const repetitionRegressionData = useMemo(() => {
    if (repetitionScatterData.length < 2) return [];
    
    const points = repetitionScatterData.map(d => ({ x: d.repetition, y: d.value }));
    const regression = calculateLinearRegression(points);
    if (!regression) return [];
    
    const values = points.map(p => p.y);
    const stdDev = calculateStdDev(values);
    
    const minX = 1;
    const maxX = repetitionScatterData.length;
    
    // Create multiple points for smooth line
    const regressionPoints = [];
    const step = Math.max(1, (maxX - minX) / 20);
    for (let x = minX; x <= maxX; x += step) {
      const y = regression.slope * x + regression.intercept;
      regressionPoints.push({
        repetition: x,
        value: y,
        upper: y + stdDev,
        lower: y - stdDev,
        stdDev
      });
    }
    
    return regressionPoints;
  }, [repetitionScatterData]);

  // Determine color based on entity (left = purple, right = orange)
  const color = entity.includes('left') ? '#9333ea' : '#ea580c';

  // Helper to format exercise names
  const formatExerciseName = (name) => {
    if (!name) return 'All';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper to format entity name for parent hand display
  const formatParentHandLabel = (entity) => {
    if (entity.includes('left')) return 'Left Hand Avg';
    return 'Right Hand Avg';
  };

  // Helper to format parent type label
  const formatParentTypeLabel = (exerciseType) => {
    if (!exerciseType) return '';
    return exerciseType.charAt(0).toUpperCase() + exerciseType.slice(1) + 's Avg';
  };

  // Format entity name for display
  const entityLabel = entity.includes('finger')
    ? `${entity.includes('left') ? 'Left' : 'Right'} Finger ${entity.split('_').pop()}`
    : entity === 'left'
    ? 'Left Hand'
    : 'Right Hand';

  const exerciseLabel = formatExerciseName(exercise) || (exerciseType ? formatExerciseName(exerciseType) + 's' : 'All');
  
  // Calculate symmetric Y domains based on regression + std dev
  const timeYDomain = getSymmetricYDomainForRegression(timeRegressionData);
  const tempoYDomain = getSymmetricYDomainForRegression(tempoRegressionData);
  const repetitionYDomain = getSymmetricYDomainForRegression(repetitionRegressionData);

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-start">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{entityLabel}</h2>
          <p className="text-sm text-gray-600">{exerciseLabel}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          aria-label="Close detailed stats"
        >
          <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Content - Horizontal Layout */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Statistics Panel and Three Charts in a Row */}
        <div className="grid grid-cols-4 gap-4">
          {/* Statistics Panel - Left Side */}
          <div className="bg-gray-50 p-4 rounded-lg flex flex-col" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Statistics</h3>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs flex-1">
              <div>
                <div className="text-gray-600">Average</div>
                <div className="font-semibold text-gray-900">{avg?.toFixed(2) || 'N/A'}ms</div>
              </div>
              <div>
                <div className="text-gray-600">Median</div>
                <div className="font-semibold text-gray-900">{median?.toFixed(2) || 'N/A'}ms</div>
              </div>
              <div>
                <div className="text-gray-600">Avg Std Dev</div>
                <div className="font-semibold text-gray-900">{avgStdDev?.toFixed(2) || 'N/A'}ms</div>
              </div>
              <div>
                <div className="text-gray-600">In Range</div>
                <div className="font-semibold text-gray-900">{inRangePercent?.toFixed(1) || 'N/A'}%</div>
              </div>
              {(parentHandAvg !== null || parentTypeAvg !== null) && (
                <>
                  <div className="col-span-2 pt-2 border-t border-gray-300"></div>
                  {parentHandAvg !== null && (
                    <div>
                      <div className="text-gray-600">{formatParentHandLabel(entity)}</div>
                      <div className="font-semibold text-gray-900">{parentHandAvg.toFixed(2)}ms</div>
                    </div>
                  )}
                  {parentTypeAvg !== null && (
                    <div>
                      <div className="text-gray-600">{formatParentTypeLabel(exerciseType)}</div>
                      <div className="font-semibold text-gray-900">{parentTypeAvg.toFixed(2)}ms</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Performance over Time */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Over Time</h3>
            {timeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-lateness-breakout-time" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <ReferenceArea y1={-5} y2={5} fill="#10b981" fillOpacity={0.1} />
                  <XAxis
                    type="number"
                    dataKey="timestamp"
                    name="Date"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(ts) => {
                      const date = new Date(ts);
                      return date.toLocaleString(undefined, { 
                        month: 'short', 
                        day: 'numeric'
                      });
                    }}
                  />
                  <YAxis 
                    type="number"
                    domain={timeYDomain}
                    ticks={generateYTicks(timeYDomain)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const date = new Date(data.timestamp);
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs font-semibold">{date.toLocaleDateString()}</p>
                            <p className="text-xs">Value: {data.value?.toFixed(2)}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw Data Points - Must come before trend line */}
                  <Scatter
                    data={timeSeriesData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {/* Std Dev Band */}
                  {timeRegressionData.length > 0 && (
                    <>
                      <Area
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-lateness-breakout-time)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={timeRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>

          {/* Performance by Tempo */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Tempo</h3>
            {tempoScatterData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-lateness-breakout-tempo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <ReferenceArea y1={-5} y2={5} fill="#10b981" fillOpacity={0.1} />
                  <XAxis 
                    type="number"
                    dataKey="tempo"
                    name="Tempo"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(value) => Math.round(value)}
                  />
                  <YAxis 
                    type="number"
                    domain={tempoYDomain}
                    ticks={generateYTicks(tempoYDomain)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs">Tempo: {data.tempo ? Math.round(data.tempo) : 'N/A'} BPM</p>
                            <p className="text-xs">Value: {data.value?.toFixed(2)}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw Data Points - Must come before trend line */}
                  <Scatter
                    data={tempoScatterData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {/* Std Dev Band */}
                  {tempoRegressionData.length > 0 && (
                    <>
                      <Area
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-lateness-breakout-tempo)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={tempoRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>

          {/* Performance by Repetition */}
          <div className="bg-gray-50 p-4 rounded-lg" style={{ height: '198px' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">By Repetition</h3>
            {repetitionScatterData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <ComposedChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="gradient-lateness-breakout-rep" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <ReferenceArea y1={-5} y2={5} fill="#10b981" fillOpacity={0.1} />
                  <XAxis 
                    type="number"
                    dataKey="repetition"
                    name="Repetition"
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tickFormatter={(value) => Math.round(value)}
                  />
                  <YAxis 
                    type="number"
                    domain={repetitionYDomain}
                    ticks={generateYTicks(repetitionYDomain)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-2 border border-gray-300 rounded shadow-sm">
                            <p className="text-xs">Rep: #{Math.round(data.repetition)}</p>
                            <p className="text-xs">Value: {data.value?.toFixed(2)}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw Data Points - Must come before trend line */}
                  <Scatter
                    data={repetitionScatterData}
                    dataKey="value"
                    fill={color}
                    fillOpacity={0.6}
                    isAnimationActive={false}
                    shape="circle"
                  />
                  {/* Std Dev Band */}
                  {repetitionRegressionData.length > 0 && (
                    <>
                      <Area
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill="url(#gradient-lateness-breakout-rep)"
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={repetitionRegressionData}
                        type="monotone"
                        dataKey="value"
                        stroke={color}
                        strokeWidth={2}
                        dot={false}
                        name="Trend"
                        isAnimationActive={false}
                      />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LatenessBreakout;


